import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import type {
  ClassificationOptions,
  LeadClassification,
  ManualLeadSource,
} from '@valutten/shared-schemas';
// Deep module import for the VALUES, not the package root, and deliberately so.
//
// `leadTaxonomy` is pure zod and client-safe, but the root barrel re-exports
// brokerIdentity / brokerOfficeRouting / clientIdentity / loanFingerprinting,
// all of which `require('crypto')`. Importing any runtime VALUE from the root
// pulls Node crypto into the browser bundle and fails the build outright with
// "Can't resolve 'crypto'". The type import above is erased by the compiler, so
// it is safe at the root. Same reasoning as OwnerSelect.tsx and
// CrmStageMappingsPage.tsx.
//
// tsc --noEmit does NOT catch this: it typechecks without bundling, so the
// failure only appears in the real client build.
import {
  MANUAL_LEAD_CHANNEL_VALUES,
  MANUAL_LEAD_SOURCE_LABELS,
} from '@valutten/shared-schemas/dist/leadTaxonomy';
import {
  classificationLabels,
  emptyClassification,
  isEmptyClassification,
  resolveClassificationOptions,
} from '@valutten/shared-schemas/dist/leadClassification';
import { LeadClassificationFields } from '../../components/leads/LeadClassificationFields';
import { buildLeadIngestUrl } from './leadIngestUrl';
import {
  Circle,
  Copy,
  Inbox,
  Phone,
  Plus,
  RefreshCw,
  Send,
  Settings2,
  UserMinus,
  UserPlus,
  Users,
  X,
} from 'lucide-react';
import { functions } from '../../lib/firebase';
import { PageHeader } from '../../components/common/PageHeader';
import {
  LeadRecord,
  LeadStatus,
  LeadStatusFilter,
  WebhookTokenConfig,
  useLeads,
} from '../../hooks/useLeads';
import { useActivities } from '../../hooks/useActivities';
import { EnrollInSequenceMenu } from '../../components/leads/EnrollInSequenceMenu';
import {
  ReassignLeadControl,
  ReassignTeamMember,
} from '../../components/leads/ReassignLeadControl';
import { LogActivityModal } from '../../components/activities/LogActivityModal';
import { LeadDetailDrawer } from '../../components/leads/LeadDetailDrawer';
import { LinkLeadToLoanControl } from '../../components/leads/LinkLeadToLoanControl';
// eDM lead tags (plan-v3 core, Slice E3): every tag control below renders
// only when edm_content is on AND the role may tag leads.
import { useAuth } from '../../hooks/useAuth';
import { canTagLeads, useEntityTags, useTagSelection } from '../../hooks/useEntityTags';
import { edmErrorMessage, isEdmContentEnabled } from '../../utils/edm';
import BulkTagBar from './edm/components/BulkTagBar';
import EntityTagCell from '../../components/tags/EntityTagCell';
import EntityTagFilter from '../../components/tags/EntityTagFilter';

/** eDM bulk-tag notice nouns. */
const LEAD_NOUN = { one: 'lead', many: 'leads' };

type OutletCtx = {
  userData?: {
    uid?: string;
    companyId?: string;
    role?: string;
    displayName?: string;
    officeIds?: string[];
    canSeeAllOffices?: boolean;
  };
  teamMembers?: ReassignTeamMember[];
  companyData?: { featureFlags?: Record<string, unknown> | null } | null;
};

const STATUS_TABS: Array<{ key: LeadStatusFilter; label: string }> = [
  { key: 'new', label: 'New' },
  { key: 'contacted', label: 'Contacted' },
  { key: 'qualified', label: 'Qualified' },
  { key: 'converted', label: 'Converted' },
  { key: 'disqualified', label: 'Disqualified' },
  { key: 'all', label: 'All' },
];

const STATUS_OPTIONS: LeadStatus[] = [
  'new',
  'contacted',
  'qualified',
  'converted',
  'disqualified',
];

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function formatRelativeTime(iso: string | null): string {
  if (!iso) return '—';
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const diffMs = Math.max(0, now - then);
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins} min ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours} hr ago`;
    const days = Math.floor(hours / 24);
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days} days ago`;
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

// Source branded colors per mockup §3. Falls back to gray for unknown
// sources so we degrade gracefully if a new source enum appears before the
// palette is updated.
const SOURCE_BRAND: Record<string, { label: string; color: string }> = {
  facebook_lead_ads: { label: 'Facebook Lead Ad', color: '#1877f2' },
  meta_ads: { label: 'Meta Ads', color: '#1877f2' },
  google_ads: { label: 'Google Ads', color: '#4285f4' },
  website_form: { label: 'Website Form', color: '#21759b' },
  referral: { label: 'Referral', color: '#8b5cf6' },
  manual: { label: 'Manual', color: '#6b7280' },
  csv_import: { label: 'CSV Import', color: '#6b7280' },
  phone: { label: 'Phone', color: '#059669' },
  monday: { label: 'Monday.com', color: '#ff3d57' },
  nodify: { label: 'Nodify', color: '#0ea5e9' },
  smokeball: { label: 'Smokeball', color: '#f97316' },
  other: { label: 'Other', color: '#6b7280' },
};

function getSourceBrand(source: string): { label: string; color: string } {
  return SOURCE_BRAND[source] ?? { label: source, color: '#6b7280' };
}

// ── SLA timer pill ─────────────────────────────────────────────────────
// Counts down to firstContactDueAt; flips amber under 50% remaining,
// red under 20%. Hides on contacted/converted leads (SLA only matters
// before first contact). Hides for queued leads (queuedUntil shown
// instead). Re-renders every 30s so the broker sees time tick down
// without a manual refresh.
const SlaTimerPill: React.FC<{ lead: LeadRecord }> = ({ lead }) => {
  const [tick, setTick] = React.useState(0);
  React.useEffect(() => {
    const id = window.setInterval(() => setTick((t) => t + 1), 30000);
    return () => window.clearInterval(id);
  }, []);
  // Tick is read to force a re-render every 30s (linter-friendly).
  void tick;

  if (lead.status !== 'new') return null;

  // Quiet-hours hold takes precedence — show when the lead becomes eligible.
  if (lead.queuedUntil) {
    const ms = new Date(lead.queuedUntil).getTime() - Date.now();
    if (ms > 0) {
      const mins = Math.ceil(ms / 60000);
      return (
        <span className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-500/15 text-blue-300 border border-blue-500/30">
          ⏸ Held {mins} min
        </span>
      );
    }
  }

  if (!lead.firstContactDueAt) return null;
  const ms = new Date(lead.firstContactDueAt).getTime() - Date.now();
  if (ms <= 0) {
    return (
      <span className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-500/15 text-red-300 border border-red-500/40 animate-pulse">
        ⏱ SLA breached
      </span>
    );
  }
  const totalMs = lead.capturedAt
    ? new Date(lead.firstContactDueAt).getTime() - new Date(lead.capturedAt).getTime()
    : ms;
  const pct = totalMs > 0 ? ms / totalMs : 1;
  const tone = pct < 0.2 ? 'red' : pct < 0.5 ? 'amber' : 'green';
  const mins = Math.ceil(ms / 60000);
  const tones: Record<string, string> = {
    green: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
    amber: 'bg-amber-500/15 text-amber-300 border-amber-500/40',
    red:   'bg-red-500/15 text-red-300 border-red-500/40 animate-pulse',
  };
  return (
    <span className={`mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold border ${tones[tone]}`}>
      ⏱ {mins} min
    </span>
  );
};

// ---------- Webhook URL modal ----------

const WebhookModal: React.FC<{
  open: boolean;
  companyId: string;
  onClose: () => void;
  onGetConfig: () => Promise<WebhookTokenConfig>;
  onGenerate: () => Promise<string>;
  onRotate: () => Promise<{
    token: string;
    generation: number;
    graceExpiresAt: string;
  }>;
  onEnableMonitoring: () => Promise<{
    monitoringEnabled: true;
    maxSilenceHours: number;
  }>;
  onSetDisabled: (disabled: boolean) => Promise<void>;
}> = ({
  open,
  companyId,
  onClose,
  onGetConfig,
  onGenerate,
  onRotate,
  onEnableMonitoring,
  onSetDisabled,
}) => {
  const [config, setConfig] = useState<WebhookTokenConfig | null>(null);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [enablingMonitoring, setEnablingMonitoring] = useState(false);
  const [confirmRotate, setConfirmRotate] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const token = config?.token ?? null;

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoadingConfig(true);
    setError(null);
    setConfirmRotate(false);
    onGetConfig()
      .then((value) => {
        if (!cancelled) setConfig(value);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : String(err));
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingConfig(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, onGetConfig]);

  // Complete, ready-to-paste URL. A no-code form tool (Unbounce, Gravity
  // Forms, ...) can POST DIRECTLY to it with no body control and no
  // middleware. Shared builder so every surface emits the same thing.
  const url = useMemo(
    () => buildLeadIngestUrl({ companyId, token }),
    [token, companyId],
  );

  const samplePayload = useMemo(() => {
    if (!token) return '';
    return JSON.stringify(
      {
        companyId,
        token,
        source: 'website_form',
        firstName: 'Jane',
        lastName: 'Doe',
        email: 'jane@example.com',
        phone: '+61400000000',
        notes: 'Wants a refinance',
        marketingConsent: true,
        consentText: 'I agree to receive communications from Entourage Finance.',
        utmSource: 'google',
        utmCampaign: 'spring_2026',
      },
      null,
      2
    );
  }, [token, companyId]);

  if (!open) return null;

  const handleGenerate = async () => {
    setGenerating(true);
    setError(null);
    try {
      const t = await onGenerate();
      setConfig({
        configured: true,
        token: t,
        disabled: false,
        generation: 1,
        rotatedAt: new Date().toISOString(),
        graceExpiresAt: null,
        monitoringEnabled: true,
        maxSilenceHours: 48,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setGenerating(false);
    }
  };

  const handleRotate = async () => {
    setRotating(true);
    setError(null);
    try {
      const result = await onRotate();
      setConfig((current) => ({
        configured: true,
        token: result.token,
        disabled: false,
        generation: result.generation,
        rotatedAt: new Date().toISOString(),
        graceExpiresAt: result.graceExpiresAt,
        monitoringEnabled: current?.monitoringEnabled ?? true,
        maxSilenceHours: current?.maxSilenceHours ?? 48,
      }));
      setConfirmRotate(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setRotating(false);
    }
  };

  const handleCopy = (value: string) => {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(value).then(() => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1500);
      });
    }
  };

  const handleEnableMonitoring = async () => {
    setEnablingMonitoring(true);
    setError(null);
    try {
      const result = await onEnableMonitoring();
      setConfig((current) =>
        current
          ? {
              ...current,
              monitoringEnabled: result.monitoringEnabled,
              maxSilenceHours: result.maxSilenceHours,
            }
          : current
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setEnablingMonitoring(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-2xl rounded-lg border border-gray-700 bg-gray-900 p-6 shadow-2xl">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-white">Lead capture webhook</h3>
            <p className="text-sm text-gray-400 mt-1">
              Paste this complete URL into your website form or webhook tool
              (Unbounce, Gravity Forms, etc.) — no code needed; it already includes
              your token, so treat the URL as confidential. For API integrations you
              can instead send <span className="font-mono">companyId</span> +{' '}
              <span className="font-mono">token</span> in the JSON body (see sample).
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        {loadingConfig ? (
          <div className="rounded-md border border-gray-700 bg-gray-800/40 p-6 text-center text-sm text-gray-400">
            <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2" />
            Loading current webhook configuration…
          </div>
        ) : !token ? (
          <div className="rounded-md border border-gray-700 bg-gray-800/40 p-6 text-center">
            <Send className="w-8 h-8 text-blue-400 mx-auto mb-3" />
            <p className="text-sm text-gray-300 mb-4">
              Generate the first secure token to enable this webhook. Once it
              exists, rotation is handled by a separate confirmed action.
            </p>
            <button
              onClick={handleGenerate}
              disabled={generating}
              className="px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white inline-flex items-center gap-2 disabled:opacity-50"
            >
              {generating && <RefreshCw className="w-4 h-4 animate-spin" />}
              {generating ? 'Generating…' : 'Generate webhook token'}
            </button>
            {error && <p className="text-xs text-red-400 mt-3">{error}</p>}
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="block text-xs uppercase tracking-wider text-gray-400 mb-1">
                Webhook URL
              </label>
              <div className="flex items-center gap-2">
                <input
                  readOnly
                  value={url}
                  className="flex-1 px-3 py-2 rounded-md bg-gray-800 border border-gray-700 text-white text-xs font-mono"
                />
                <button
                  onClick={() => handleCopy(url)}
                  className="px-3 py-2 rounded-md bg-gray-700 hover:bg-gray-600 text-white text-xs inline-flex items-center gap-1"
                >
                  <Copy className="w-3.5 h-3.5" />
                  Copy
                </button>
              </div>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-gray-400 mb-1">
                Token (also include in JSON body as &quot;token&quot;)
              </label>
              <div className="flex items-center gap-2">
                <input
                  readOnly
                  value={token}
                  className="flex-1 px-3 py-2 rounded-md bg-gray-800 border border-gray-700 text-white text-xs font-mono"
                />
                <button
                  onClick={() => handleCopy(token)}
                  className="px-3 py-2 rounded-md bg-gray-700 hover:bg-gray-600 text-white text-xs inline-flex items-center gap-1"
                >
                  <Copy className="w-3.5 h-3.5" />
                  Copy
                </button>
              </div>
              {copied && <p className="text-xs text-emerald-400 mt-1">Copied to clipboard.</p>}
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-gray-400 mb-1">
                Sample POST body
              </label>
              <pre className="px-3 py-2 rounded-md bg-gray-800 border border-gray-700 text-gray-200 text-xs font-mono overflow-x-auto">
                {samplePayload}
              </pre>
            </div>
            <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-200">
              This is the current token. Viewing or copying it does not rotate
              it. {config?.monitoringEnabled
                ? `Production monitoring alerts VALUTTEN after the first rejected token and after ${config.maxSilenceHours} hours without a genuine lead.`
                : 'Lead-silence monitoring is not yet enabled for this existing token; rotating it enables monitoring. Token-mismatch alerts are active independently.'}
            </div>
            {!config?.monitoringEnabled && (
              <button
                type="button"
                onClick={handleEnableMonitoring}
                disabled={enablingMonitoring}
                className="px-3 py-2 rounded-md border border-amber-500/50 bg-amber-500/10 hover:bg-amber-500/20 text-xs text-amber-100 inline-flex items-center gap-2 disabled:opacity-50"
              >
                {enablingMonitoring && (
                  <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                )}
                {enablingMonitoring
                  ? 'Enabling monitoring…'
                  : 'Enable lead-silence monitoring'}
              </button>
            )}
            {config?.graceExpiresAt && (
              <div className="rounded-md border border-blue-500/30 bg-blue-500/5 p-3 text-xs text-blue-200">
                The previous token remains valid until{' '}
                {new Date(config.graceExpiresAt).toLocaleString('en-AU')}.
                Update every upstream integration before then.
              </div>
            )}
            {confirmRotate && (
              <div className="rounded-md border border-red-500/40 bg-red-500/5 p-4">
                <p className="text-sm font-semibold text-red-200">
                  Confirm token rotation
                </p>
                <p className="text-xs text-red-100/80 mt-2">
                  A new token becomes current immediately. The existing token
                  continues working for 24 hours, then every integration still
                  using it will receive HTTP 401 and stop delivering leads.
                </p>
                <div className="flex justify-end gap-2 mt-4">
                  <button
                    type="button"
                    onClick={() => setConfirmRotate(false)}
                    disabled={rotating}
                    className="px-3 py-2 rounded-md bg-gray-700 hover:bg-gray-600 text-xs text-white disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleRotate()}
                    disabled={rotating}
                    className="px-3 py-2 rounded-md bg-red-600 hover:bg-red-700 text-xs text-white inline-flex items-center gap-1 disabled:opacity-50"
                  >
                    {rotating && (
                      <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    )}
                    {rotating ? 'Rotating…' : 'Rotate token'}
                  </button>
                </div>
              </div>
            )}
            {error && <p className="text-xs text-red-400">{error}</p>}
            <div className="flex items-center justify-between pt-2">
              <button
                onClick={async () => {
                  await onSetDisabled(true);
                  onClose();
                }}
                className="text-xs text-gray-400 hover:text-red-400"
              >
                Disable webhook
              </button>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setConfirmRotate(true)}
                  disabled={rotating || confirmRotate}
                  className="px-3 py-2 rounded-md border border-red-500/40 text-red-300 hover:bg-red-500/10 text-xs disabled:opacity-50"
                >
                  Rotate token
                </button>
                <button
                  onClick={onClose}
                  className="px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ---------- Lead row ----------

// "Decline & reroute" modal — collects optional reason, calls onConfirm.
// Mirrors the existing WebhookModal pattern in this file (inline error
// state, no global toast). Surfaces friendly text for the two outcomes:
// reassigned (next broker picked up) vs pool_exhausted (no eligible —
// admin will assign manually).
const DeclineLeadModal: React.FC<{
  open: boolean;
  onConfirm: (reason: string) => Promise<{
    outcome: 'reassigned' | 'pool_exhausted';
    newAssigneeUid: string | null;
  }>;
  onCancel: () => void;
}> = ({ open, onConfirm, onCancel }) => {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-md rounded-lg border border-gray-700 bg-gray-900 p-6 shadow-2xl">
        <h3 className="text-lg font-bold text-white">Decline & reroute lead</h3>
        <p className="text-sm text-gray-400 mt-1">
          We'll pass this lead to the next broker in the rotation. You won't
          see it again. Optional: add a short reason for the audit log.
        </p>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value.slice(0, 500))}
          rows={3}
          maxLength={500}
          placeholder="Reason (optional, max 500 chars)"
          className="mt-3 w-full px-3 py-2 rounded-md bg-gray-800 border border-gray-700 text-white text-sm"
        />
        {error && (
          <p className="mt-2 text-sm text-red-400">{error}</p>
        )}
        <div className="mt-4 flex justify-end gap-2">
          <button
            onClick={() => {
              setReason('');
              setError(null);
              onCancel();
            }}
            disabled={submitting}
            className="px-3 py-1.5 rounded-md bg-gray-700 hover:bg-gray-600 text-white text-sm"
          >
            Cancel
          </button>
          <button
            onClick={async () => {
              setSubmitting(true);
              setError(null);
              try {
                await onConfirm(reason.trim());
                setReason('');
              } catch (err) {
                setError(err instanceof Error ? err.message : String(err));
              } finally {
                setSubmitting(false);
              }
            }}
            disabled={submitting}
            className="px-3 py-1.5 rounded-md bg-amber-600 hover:bg-amber-500 text-white text-sm inline-flex items-center gap-1.5 disabled:opacity-50"
          >
            {submitting && <RefreshCw className="w-3.5 h-3.5 animate-spin" />}
            Confirm decline
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------- Lead row ----------

const BROKER_FAMILY_ROLES = new Set([
  'service_staff',
  'broker',
  'broker_assistant',
  'credit_analyst',
]);

const ADMIN_ROLES = new Set(['super_admin', 'administrator', 'office_admin']);

/**
 * Roles that may work the inbox itself: log a contact, add a note, reassign.
 *
 * Marketing belongs here. It is the designated Lead Capture operator, the
 * server already accepts it for `updateLead` (including reassignment) and
 * grants it company-wide lead scope in `assertLeadVisible`, and
 * `activityAction` now accepts it for LEAD-linked activity. Leaving it out of
 * this set was a client-only gap that hid the buttons from the one role whose
 * job the inbox is. Commission visibility is unaffected: marketing keeps
 * `hideCommissions: true` and the server refuses it any loan-linked activity.
 */
const LEAD_OPERATOR_ROLES = new Set([...ADMIN_ROLES, 'marketing']);

const LeadRow: React.FC<{
  lead: LeadRecord;
  companyId: string | undefined;
  currentUid: string | undefined;
  currentName: string | undefined;
  currentRole: string | undefined;
  teamMembers: ReassignTeamMember[];
  scopeOfficeIds: string[] | undefined;
  onUpdate: (args: {
    leadId: string;
    status?: LeadStatus;
    assignedBrokerId?: string | null;
    assignedBrokerName?: string | null;
  }) => Promise<void>;
  onDecline: (args: { leadId: string; reason?: string }) => Promise<{
    outcome: 'reassigned' | 'pool_exhausted';
    newAssigneeUid: string | null;
  }>;
  onLogContact: (lead: LeadRecord) => void;
  onOpenDetail: (lead: LeadRecord) => void;
  /** eDM tags (edm_content only). Absent = the row renders exactly as before. */
  tagging?: { selected: boolean; onToggle: () => void; tags: string[] | null | undefined };
}> = ({
  lead,
  companyId,
  currentUid,
  currentName,
  currentRole,
  teamMembers,
  scopeOfficeIds,
  onUpdate,
  onDecline,
  onLogContact,
  onOpenDetail,
  tagging,
}) => {
  const [busy, setBusy] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [rowError, setRowError] = useState<string | null>(null);

  const isBrokerFamilyMember = !!currentRole && BROKER_FAMILY_ROLES.has(currentRole);
  const isLeadOperator = !!currentRole && LEAD_OPERATOR_ROLES.has(currentRole);

  const canDecline =
    lead.status === 'new' &&
    !!currentUid &&
    lead.assignedBrokerId === currentUid &&
    isBrokerFamilyMember;

  // "Log contact" — available to the lead owner (broker-family assigned to it)
  // or to an inbox operator in scope. Mirrors the server's updateLead
  // visibility.
  const canLogContact =
    isLeadOperator || (isBrokerFamilyMember && !!currentUid && lead.assignedBrokerId === currentUid);

  // Reassign is operator-only (broker-family is blocked server-side).
  const canReassign = isLeadOperator;

  const fullName =
    [lead.firstName, lead.lastName].filter(Boolean).join(' ') || lead.email || '—';

  // try/finally without a catch left a rejected updateLead silently
  // swallowed: the spinner stopped and the row snapped back to its old
  // status with no explanation, which reads as "the app lost my change".
  // Surface the failure on the row instead.
  const handleStatusChange = async (status: LeadStatus) => {
    setBusy(true);
    setRowError(null);
    try {
      await onUpdate({ leadId: lead.leadId, status });
    } catch (err) {
      console.error('[LeadRow] status change failed', err);
      setRowError(
        err instanceof Error ? err.message : 'Could not update this lead.'
      );
    } finally {
      setBusy(false);
    }
  };

  const handleClaim = async () => {
    setBusy(true);
    setRowError(null);
    try {
      await onUpdate({
        leadId: lead.leadId,
        assignedBrokerId: currentUid ?? null,
        assignedBrokerName: currentName ?? null,
      });
    } catch (err) {
      console.error('[LeadRow] claim failed', err);
      setRowError(
        err instanceof Error ? err.message : 'Could not claim this lead.'
      );
    } finally {
      setBusy(false);
    }
  };

  const brand = getSourceBrand(lead.source);
  const campaign =
    lead.attribution?.utmCampaign ||
    (lead.source === 'referral' ? 'Partner Referral' : '(organic)');

  return (
    <tr className="hover:bg-white/[0.02]">
      {tagging && (
        <td className="px-3 py-3">
          <input
            type="checkbox"
            checked={tagging.selected}
            onChange={tagging.onToggle}
            disabled={tagging.tags === null}
            title={tagging.tags === null ? 'You cannot tag this lead' : undefined}
            aria-label={`Select ${fullName}`}
            className="accent-blue-500"
          />
        </td>
      )}
      <td className="px-3 py-3 text-gray-300 text-xs whitespace-nowrap">
        <div>{formatRelativeTime(lead.capturedAt)}</div>
        <div className="text-[0.65rem] text-gray-500">
          {formatDate(lead.capturedAt)}
        </div>
        <SlaTimerPill lead={lead} />
      </td>
      <td className="px-3 py-3 text-white">
        <button
          type="button"
          onClick={() => onOpenDetail(lead)}
          title="Open lead detail"
          className="text-left hover:text-blue-300 focus:outline-none focus:text-blue-300"
        >
          <div className="font-medium underline-offset-2 hover:underline">
            {fullName}
          </div>
        </button>
        <div className="text-[0.7rem] text-gray-500">
          {lead.email && <span>{lead.email}</span>}
          {lead.email && lead.phone && <span> · </span>}
          {lead.phone && <span>{lead.phone}</span>}
        </div>
      </td>
      <td className="px-3 py-3">
        <span
          className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium"
          style={{
            background: `${brand.color}20`,
            color: brand.color,
            border: `1px solid ${brand.color}50`,
          }}
        >
          <Circle
            className="w-1.5 h-1.5 flex-shrink-0"
            style={{ fill: brand.color, color: brand.color }}
          />
          {brand.label}
        </span>
      </td>
      <td className="px-3 py-3 text-gray-300 text-xs">{campaign}</td>
      <td className="px-3 py-3 text-gray-300 text-xs">
        {lead.assignedBrokerName ? (
          lead.assignedBrokerName
        ) : (
          <span className="text-amber-400">Unassigned</span>
        )}
      </td>
      <td className="px-3 py-3">
        <select
          value={lead.status}
          onChange={(e) => handleStatusChange(e.target.value as LeadStatus)}
          disabled={busy}
          className="px-2 py-1 rounded-md bg-gray-800 border border-gray-700 text-white text-xs focus:outline-none focus:border-blue-500"
        >
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </td>
      {tagging && (
        <td className="px-3 py-3">
          <EntityTagCell entityId={lead.leadId} tags={tagging.tags} />
        </td>
      )}
      <td className="px-3 py-3 text-right">
        <div className="inline-flex items-center gap-1.5 justify-end flex-wrap">
          {!lead.assignedBrokerId && currentUid && (
            <button
              onClick={handleClaim}
              disabled={busy}
              className="px-2 py-1 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-xs inline-flex items-center gap-1 disabled:opacity-50"
            >
              <UserPlus className="w-3 h-3" />
              Claim
            </button>
          )}
          {canLogContact && (
            <button
              onClick={() => onLogContact(lead)}
              disabled={busy}
              title="Log a contact touchpoint for this lead"
              className="px-2 py-1 rounded-md bg-gray-700 hover:bg-gray-600 text-white text-xs inline-flex items-center gap-1 disabled:opacity-50"
            >
              <Phone className="w-3 h-3" />
              Log contact
            </button>
          )}
          {canDecline && (
            <button
              onClick={() => setDeclineOpen(true)}
              disabled={busy}
              title="Decline this lead and reroute to next broker"
              className="px-2 py-1 rounded-md bg-amber-700/40 hover:bg-amber-700/70 text-amber-200 text-xs inline-flex items-center gap-1 border border-amber-600/40 disabled:opacity-50"
            >
              <UserMinus className="w-3 h-3" />
              Decline
            </button>
          )}
          {canReassign && (
            <ReassignLeadControl
              currentAssignedBrokerId={lead.assignedBrokerId}
              teamMembers={teamMembers}
              scopeOfficeIds={scopeOfficeIds}
              onReassign={async (member) => {
                setBusy(true);
                setRowError(null);
                try {
                  await onUpdate({
                    leadId: lead.leadId,
                    assignedBrokerId: member.id,
                    assignedBrokerName: member.displayName,
                  });
                } catch (err) {
                  console.error('[LeadRow] reassign failed', err);
                  setRowError(
                    err instanceof Error
                      ? err.message
                      : 'Could not reassign this lead.'
                  );
                } finally {
                  setBusy(false);
                }
              }}
            />
          )}
          <EnrollInSequenceMenu companyId={companyId} leadId={lead.leadId} />
        </div>
        {rowError && (
          <div
            role="alert"
            className="mt-2 rounded border border-red-500/30 bg-red-500/10 px-2 py-1 text-[0.65rem] text-red-300"
          >
            {rowError}
          </div>
        )}
      </td>
      {canDecline && (
        // Modal lives outside the actions <td> but inside the row's
        // logical scope so it captures `lead.leadId` correctly.
        // Renders into a portal-like fixed position via its own JSX.
        <td className="hidden">
          <DeclineLeadModal
            open={declineOpen}
            onCancel={() => setDeclineOpen(false)}
            onConfirm={async (reason) => {
              const result = await onDecline({
                leadId: lead.leadId,
                reason: reason || undefined,
              });
              setDeclineOpen(false);
              return result;
            }}
          />
        </td>
      )}
    </tr>
  );
};

// ---------- Add-lead modal ----------

/**
 * Two groups, because Source was answering two different questions at once:
 * HOW the enquiry reached the business, and WHICH system it was copied out of.
 * "Arrived by email" and "keyed out of Smokeball" were mutually exclusive
 * answers, which lost the channel every time a lead came from another CRM.
 */
const CHANNEL_OPTIONS: Array<{ value: ManualLeadSource; label: string }> =
  (MANUAL_LEAD_CHANNEL_VALUES as readonly string[]).map((v) => ({
    value: v as ManualLeadSource,
    label: MANUAL_LEAD_SOURCE_LABELS[v] ?? v,
  }));

const IMPORTED_FROM_OPTIONS: Array<{ value: ManualLeadSource; label: string }> = [
  { value: 'manual', label: 'Manual entry' },
  { value: 'monday', label: 'Monday.com' },
  { value: 'nodify', label: 'Nodify' },
  { value: 'smokeball', label: 'Smokeball' },
];

interface CrmReferrerOption {
  id: string;
  name: string;
  organisation?: string;
}

interface AssignableMember {
  id: string;
  displayName: string;
}

interface TaxonomyCategory {
  id: string;
  label: string;
  active?: boolean;
}

// Hand-key a lead captured off-platform (inbound call, or another CRM). Posts
// to the createManualLead callable, which routes/triages it exactly like a
// captured lead. Service categories are pulled from the tenant's own
// lead_taxonomy so the picker matches the routing config; falls back to a
// free-text entry if the taxonomy can't be read.
const AddLeadModal: React.FC<{
  open: boolean;
  companyId: string;
  onClose: () => void;
  onCreate: (args: {
    firstName: string;
    lastName: string;
    email?: string;
    phone?: string;
    serviceCategory: string;
    source: ManualLeadSource;
    notes?: string;
    referrerName?: string;
    referrerOrganisation?: string;
    mycrmReferrerId?: string;
    assignToUid?: string;
    classification?: LeadClassification;
  }) => Promise<{ leadId: string; deduped: boolean }>;
}> = ({ open, companyId, onClose, onCreate }) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [serviceCategory, setServiceCategory] = useState('');
  const [source, setSource] = useState<ManualLeadSource>('phone');
  const [notes, setNotes] = useState('');
  const [referrerName, setReferrerName] = useState('');
  // Set only when the typed name was chosen from the tenant's own MyCRM
  // referrer list. Cleared on every keystroke so an edited name can never keep
  // a stale link to a different referrer.
  const [mycrmReferrerId, setMycrmReferrerId] = useState<string | null>(null);
  const [referrerOrg, setReferrerOrg] = useState<string | null>(null);
  const [crmReferrers, setCrmReferrers] = useState<CrmReferrerOption[]>([]);
  const [assignToUid, setAssignToUid] = useState('');
  const [members, setMembers] = useState<AssignableMember[]>([]);
  const [categories, setCategories] = useState<TaxonomyCategory[]>([]);
  // The five classification questions: the tenant's options per service
  // (defaults apply to the mortgage category) and the operator's answers.
  const [taxonomyClassification, setTaxonomyClassification] = useState<Record<string, ClassificationOptions> | null>(null);
  const [classification, setClassification] = useState<LeadClassification>(emptyClassification());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Referrer suggestions + assignable members. Both are ASSISTS: if either
  // call fails the modal still works (free-text referrer, unassigned lead), so
  // neither is allowed to surface an error or block submission.
  useEffect(() => {
    if (!open || !companyId) return;
    let cancelled = false;

    httpsCallable<{ companyId: string }, { available: boolean; referrers: CrmReferrerOption[] }>(
      functions,
      'listCrmReferrers',
    )({ companyId })
      .then((r) => { if (!cancelled) setCrmReferrers(r.data?.referrers ?? []); })
      .catch(() => { if (!cancelled) setCrmReferrers([]); });

    httpsCallable<{ companyId: string }, { brokers?: AssignableMember[] }>(
      functions,
      'getBrokerLabels',
    )({ companyId })
      .then((r) => {
        if (cancelled) return;
        const list = r.data?.brokers ?? [];
        setMembers([...list].sort((a, b) => a.displayName.localeCompare(b.displayName)));
      })
      .catch(() => { if (!cancelled) setMembers([]); });

    return () => { cancelled = true; };
  }, [open, companyId]);

  // Load the tenant's service categories when the modal opens.
  useEffect(() => {
    if (!open || !companyId) return;
    let cancelled = false;
    const fn = httpsCallable<
      { action: 'getLeadTaxonomy'; companyId: string },
      { serviceCategories?: TaxonomyCategory[]; classification?: Record<string, ClassificationOptions> | null }
    >(functions, 'leadCaptureAction');
    fn({ action: 'getLeadTaxonomy', companyId })
      .then((r) => {
        if (cancelled) return;
        const cats = (r.data.serviceCategories ?? []).filter(
          (c) => c.active !== false
        );
        setCategories(cats);
        setTaxonomyClassification(r.data.classification ?? null);
        setServiceCategory((prev) => prev || cats[0]?.id || '');
      })
      .catch((err) => {
        // Non-fatal: operator can still type a category id below.
        console.warn('[AddLeadModal] getLeadTaxonomy failed', err);
      });
    return () => {
      cancelled = true;
    };
  }, [open, companyId]);

  if (!open) return null;

  const classificationOptions = resolveClassificationOptions(taxonomyClassification, serviceCategory.trim() || null);

  const reset = () => {
    setClassification(emptyClassification());
    setFirstName('');
    setLastName('');
    setEmail('');
    setPhone('');
    setServiceCategory(categories[0]?.id ?? '');
    setSource('phone');
    setNotes('');
    setReferrerName('');
    setMycrmReferrerId(null);
    setReferrerOrg(null);
    setAssignToUid('');
    setError(null);
    setSuccess(null);
  };

  const handleSubmit = async () => {
    setError(null);
    setSuccess(null);
    if (!firstName.trim() || !lastName.trim()) {
      setError('First and last name are required.');
      return;
    }
    if (!email.trim() && !phone.trim()) {
      setError('Provide at least an email or a phone number.');
      return;
    }
    if (!serviceCategory.trim()) {
      setError('Choose a service category.');
      return;
    }
    // Mirrors the createManualLead schema's refine, so the operator sees the
    // rule here rather than as a rejected submit.
    if (source === 'referral' && !referrerName.trim()) {
      setError('Add who referred them, or change the source.');
      return;
    }
    setSubmitting(true);
    try {
      // Belt-and-braces against the callable undefined -> null wire encoding:
      // setting a key to `undefined` still serialises as `null`, which a strict
      // server schema rejects. OMIT the key instead, so it is never sent.
      // (createManualLead now also accepts null via callableOptional.)
      const payload: Parameters<typeof onCreate>[0] = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        serviceCategory: serviceCategory.trim(),
        source,
      };
      if (email.trim()) payload.email = email.trim();
      if (phone.trim()) payload.phone = phone.trim();
      if (notes.trim()) payload.notes = notes.trim();
      if (referrerName.trim()) {
        payload.referrerName = referrerName.trim();
        // Only send the link when the name still matches the picked suggestion.
        if (mycrmReferrerId) payload.mycrmReferrerId = mycrmReferrerId;
        if (referrerOrg) payload.referrerOrganisation = referrerOrg;
      }
      if (assignToUid) payload.assignToUid = assignToUid;
      // Omitted when unanswered, so the lead carries no classification field.
      if (classificationOptions && !isEmptyClassification(classification)) {
        payload.classification = classification;
      }

      const result = await onCreate(payload);
      if (result.deduped) {
        setSuccess('This contact already exists as a lead; opened the existing record.');
      } else {
        setSuccess('Lead added.');
      }
      reset();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    'w-full px-3 py-2 rounded-md bg-gray-800 border border-gray-700 text-white text-sm focus:outline-none focus:border-blue-500';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-lg rounded-lg border border-gray-700 bg-gray-900 p-6 shadow-2xl">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-white">Add lead</h3>
            <p className="text-sm text-gray-400 mt-1">
              Hand-key a lead from an inbound call or another CRM. It routes and
              triages exactly like a captured lead.
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs uppercase tracking-wider text-gray-400 mb-1">
                First name
              </label>
              <input
                value={firstName}
                onChange={(e) => setFirstName(e.target.value.slice(0, 200))}
                className={inputCls}
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-gray-400 mb-1">
                Last name
              </label>
              <input
                value={lastName}
                onChange={(e) => setLastName(e.target.value.slice(0, 200))}
                className={inputCls}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs uppercase tracking-wider text-gray-400 mb-1">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value.slice(0, 300))}
                className={inputCls}
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-gray-400 mb-1">
                Phone
              </label>
              <input
                value={phone}
                onChange={(e) => setPhone(e.target.value.slice(0, 50))}
                className={inputCls}
              />
            </div>
          </div>
          <p className="text-[11px] text-gray-500 -mt-1">
            Provide at least an email or a phone number.
          </p>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs uppercase tracking-wider text-gray-400 mb-1">
                Service category
              </label>
              {categories.length > 0 ? (
                <select
                  value={serviceCategory}
                  onChange={(e) => {
                    setServiceCategory(e.target.value);
                    // Options are per service; answers to another service's
                    // questions would only be dropped by the server.
                    setClassification(emptyClassification());
                  }}
                  className={inputCls}
                >
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.label}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  value={serviceCategory}
                  onChange={(e) => setServiceCategory(e.target.value.slice(0, 64))}
                  placeholder="e.g. finance"
                  className={inputCls}
                />
              )}
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-gray-400 mb-1">
                Source
              </label>
              <select
                value={source}
                onChange={(e) => setSource(e.target.value as ManualLeadSource)}
                className={inputCls}
              >
                <optgroup label="How they reached us">
                  {CHANNEL_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </optgroup>
                <optgroup label="Imported from">
                  {IMPORTED_FROM_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </optgroup>
              </select>
            </div>
          </div>

          {classificationOptions && (
            <div className="rounded-md border border-gray-800 bg-gray-900/40 p-3">
              <div className="text-xs uppercase tracking-wider text-gray-400 mb-2">About the loan (optional)</div>
              <LeadClassificationFields
                options={classificationOptions}
                value={classification}
                onChange={setClassification}
                disabled={submitting}
                idPrefix="add-lead-classification"
              />
            </div>
          )}

          {source === 'referral' && (
            <div>
              <label className="block text-xs uppercase tracking-wider text-gray-400 mb-1">
                Referred by
              </label>
              <input
                value={referrerName}
                list="crm-referrer-options"
                onChange={(e) => {
                  const v = e.target.value.slice(0, 200);
                  setReferrerName(v);
                  // Re-match on every keystroke. Editing away from the chosen
                  // suggestion must drop the link, or the lead would carry a
                  // referrer id that no longer matches the name shown.
                  const hit = crmReferrers.find(
                    (r) => r.name.toLowerCase() === v.trim().toLowerCase(),
                  );
                  setMycrmReferrerId(hit ? hit.id : null);
                  setReferrerOrg(hit?.organisation ?? null);
                }}
                placeholder="e.g. Ray White Brisbane"
                className={inputCls}
              />
              <datalist id="crm-referrer-options">
                {crmReferrers.map((r) => (
                  <option key={r.id} value={r.name}>
                    {r.organisation ?? ''}
                  </option>
                ))}
              </datalist>
              <p className="mt-1 text-xs text-gray-500">
                {mycrmReferrerId
                  ? 'Linked to this referrer in MyCRM.'
                  : crmReferrers.length > 0
                    ? 'Pick a suggestion to link it to MyCRM, or type any name.'
                    : 'Type any name. It is saved and reported in Valutten.'}
              </p>
            </div>
          )}

          <div>
            <label className="block text-xs uppercase tracking-wider text-gray-400 mb-1">
              Assign to
            </label>
            <select
              value={assignToUid}
              onChange={(e) => setAssignToUid(e.target.value)}
              className={inputCls}
            >
              <option value="">Auto-assign by routing rules</option>
              {members.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.displayName}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">
              Choosing someone here beats the rotation, and sends the lead to
              them in MyCRM too.
            </p>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wider text-gray-400 mb-1">
              Notes
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value.slice(0, 5000))}
              rows={3}
              placeholder="Optional context (what they enquired about, timeline, etc.)"
              className={inputCls}
            />
          </div>

          {error && <p className="text-sm text-red-400">{error}</p>}
          {success && <p className="text-sm text-emerald-400">{success}</p>}

          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={onClose}
              disabled={submitting}
              className="px-3 py-1.5 rounded-md bg-gray-700 hover:bg-gray-600 text-white text-sm"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="px-4 py-1.5 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm inline-flex items-center gap-1.5 disabled:opacity-50"
            >
              {submitting && <RefreshCw className="w-3.5 h-3.5 animate-spin" />}
              Add lead
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------- Lead classification (detail drawer) ----------

/**
 * The lead's five classification answers in the detail drawer: read-only
 * labels, and an editor for anyone who may act on the lead. Saves through
 * updateLead, which checks the answers against the tenant's options for the
 * lead's service and drops unknown ones. Renders nothing for a service that
 * asks no classification questions and has no stored answers.
 */
const LeadClassificationPanel: React.FC<{
  lead: LeadRecord;
  companyId: string;
  canEdit: boolean;
  onSave: (classification: LeadClassification | null) => Promise<void>;
}> = ({ lead, companyId, canEdit, onSave }) => {
  const [taxonomyClassification, setTaxonomyClassification] = useState<Record<string, ClassificationOptions> | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<LeadClassification>(emptyClassification());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    httpsCallable<
      { action: 'getLeadTaxonomy'; companyId: string },
      { classification?: Record<string, ClassificationOptions> | null }
    >(functions, 'leadCaptureAction')({ action: 'getLeadTaxonomy', companyId })
      .then((r) => { if (!cancelled) setTaxonomyClassification(r.data.classification ?? null); })
      .catch(() => { /* defaults still apply below */ })
      .finally(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, [companyId]);

  // A different lead opened in the same drawer starts out of edit mode.
  useEffect(() => {
    setEditing(false);
    setError(null);
  }, [lead.leadId]);

  const options = resolveClassificationOptions(taxonomyClassification, lead.serviceCategory);
  const stored = lead.classification ?? null;
  if (!loaded) return null;
  if (!options && isEmptyClassification(stored)) return null;

  const labels = classificationLabels(stored, options);
  const rows: Array<[string, string | null]> = [
    ['Loan purpose', labels.loanPurpose],
    ['Property use', labels.propertyUse],
    ['Situation', labels.borrowerSituations.length > 0 ? labels.borrowerSituations.join(', ') : null],
    ['Loan structure', labels.loanStructure],
    ['Loan amount', labels.loanAmount],
  ];

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSave(isEmptyClassification(draft) ? null : draft);
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-2" data-testid="lead-classification-panel">
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-wider text-gray-500">About the loan</div>
        {canEdit && options && !editing && (
          <button
            type="button"
            onClick={() => { setDraft(stored ? { ...stored } : emptyClassification()); setEditing(true); }}
            className="text-xs text-blue-400 hover:text-blue-300"
          >
            {isEmptyClassification(stored) ? 'Add answers' : 'Edit'}
          </button>
        )}
      </div>
      {editing && options ? (
        <div className="space-y-3">
          <LeadClassificationFields
            options={options}
            value={draft}
            onChange={setDraft}
            disabled={saving}
            idPrefix={`lead-${lead.leadId}-classification`}
          />
          {error && <div className="text-xs text-red-300">{error}</div>}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className="px-3 py-1.5 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-xs disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              onClick={() => setEditing(false)}
              disabled={saving}
              className="px-3 py-1.5 rounded-md border border-gray-700 text-gray-300 text-xs"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : isEmptyClassification(stored) ? (
        <div className="text-sm text-gray-500">Not answered</div>
      ) : (
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
          {rows.filter(([, v]) => v).map(([k, v]) => (
            <React.Fragment key={k}>
              <dt className="text-gray-500">{k}</dt>
              <dd className="text-gray-100 break-words">{v}</dd>
            </React.Fragment>
          ))}
        </dl>
      )}
    </div>
  );
};

// ---------- Main page ----------

const LeadsPage: React.FC = () => {
  const { userData, teamMembers, companyData } = useOutletContext<OutletCtx>();
  const companyId = userData?.companyId ?? '';
  const role = userData?.role ?? '';
  const canManageWebhook = role === 'super_admin' || role === 'administrator';
  // Manual lead entry is available to the same operators the callable admits:
  // super_admin / administrator / marketing (marketing is the CRM operator).
  const canAddLead =
    role === 'super_admin' || role === 'administrator' || role === 'marketing';
  // Brokers are auto-scoped to their own leads on the server. Hiding the
  // "Mine only" toggle for them avoids implying there's any other view they
  // could switch to.
  const isBrokerFamily = role === 'broker' || role === 'broker_assistant' || role === 'credit_analyst' || role === 'service_staff';

  // Office-scope for the reassign picker. Full-access roles (super_admin /
  // canSeeAllOffices) get the whole company list; office-scoped admins only
  // see members in their officeIds. The server is the real authority.
  const scopeOfficeIds =
    role === 'super_admin' || userData?.canSeeAllOffices
      ? undefined
      : (userData?.officeIds ?? []);
  const teamMemberList = teamMembers ?? [];

  const {
    leads,
    loading,
    error,
    refresh,
    statusCounts,
    hasMore,
    loadingMore,
    loadMore,
    statusFilter,
    setStatusFilter,
    assignedToMe,
    setAssignedToMe,
    tagNorm,
    setTagNorm,
    updateLead,
    declineLead,
    generateWebhookToken,
    getWebhookTokenConfig,
    rotateWebhookToken,
    enableWebhookMonitoring,
    setWebhookDisabled,
    createManualLead,
    getInboxStats,
    linkLeadToLoan,
  } = useLeads(companyId);

  const { logActivity } = useActivities(companyId);

  // ---- eDM lead tags (edm_content). The server (edmTagAction, listLeads
  // tagNorm) is the authority; this only hides what it would refuse.
  const { isBrokerFilteredAdmin } = useAuth();
  const tagsOn =
    isEdmContentEnabled(companyData?.featureFlags) && canTagLeads(role, isBrokerFilteredAdmin === true);
  const loadedLeadIds = useMemo(() => leads.map((l) => l.leadId), [leads]);
  const leadTags = useEntityTags({ companyId, kind: 'lead', enabled: tagsOn, ids: loadedLeadIds });
  // A tag change can move rows in or out of the active tag filter.
  const onTagsApplied = useCallback(() => {
    if (tagNorm) refresh();
  }, [tagNorm, refresh]);
  // Rows the server reported as outside the caller's tagging scope are not selectable.
  const taggableLeadIds = useMemo(
    () => loadedLeadIds.filter((id) => leadTags.tagsById[id] !== null),
    [loadedLeadIds, leadTags.tagsById]
  );
  const tagSel = useTagSelection(taggableLeadIds, leadTags.bulkTag, LEAD_NOUN, onTagsApplied);

  // Page-level "Log contact" modal — a single instance driven by which lead
  // is being logged (avoids one modal per row).
  const [logContactLead, setLogContactLead] = useState<LeadRecord | null>(null);

  // Lead detail drawer — opened by clicking a lead's name. Keeps the inbox
  // table state intact (not a route). Re-derives from `leads` on refresh so the
  // drawer reflects the latest assignment/status after an action.
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null);
  const selectedLead = useMemo(
    () => leads.find((l) => l.leadId === selectedLeadId) ?? null,
    [leads, selectedLeadId]
  );
  // Bumped after a page-level Log-contact submit so the open drawer re-fetches
  // its timeline (the modal write doesn't bump the drawer's own refresh).
  const [timelineRefreshSignal, setTimelineRefreshSignal] = useState(0);

  // Whether the current user may act (log contact / add note) on a lead —
  // mirrors the row's canLogContact: admins-in-scope, or the broker-family
  // owner. The server is the real authority.
  const canActOnLead = useCallback(
    (lead: LeadRecord | null): boolean => {
      if (!lead || !role) return false;
      if (LEAD_OPERATOR_ROLES.has(role)) return true;
      return (
        BROKER_FAMILY_ROLES.has(role) &&
        !!userData?.uid &&
        lead.assignedBrokerId === userData.uid
      );
    },
    [role, userData?.uid]
  );

  // Server-rendered inbox stats from getInboxStats. Populated async; falls
  // back to client-side derivation until the first response arrives. Both
  // produce the same shape so the stat tiles swap transparently.
  const [serverStats, setServerStats] = React.useState<{
    todayCount: number;
    weekCount: number;
    avgResponseMinutes: number | null;
    assignmentCoveragePercent: number;
    totalCount: number;
    truncated: boolean;
  } | null>(null);
  React.useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    getInboxStats()
      .then((r) => {
        if (!cancelled) setServerStats(r);
      })
      .catch((err) => {
        // Non-fatal — client-side fallback continues to render the tiles.
        console.warn('[LeadsPage] getInboxStats failed; using client fallback', err);
      });
    return () => {
      cancelled = true;
    };
  }, [companyId, getInboxStats]);

  const [webhookOpen, setWebhookOpen] = useState(false);
  const [addLeadOpen, setAddLeadOpen] = useState(false);

  // Status chip totals come from the server (`getLeadStatusCounts`), counted
  // within the caller's scope. They used to be derived from `leads`, which is
  // one page of ONE status, so with the default 'new' filter every other chip
  // read 0.

  // Client-side fallback — covers the gap between mount and server stats
  // response. Once serverStats is populated we swap to those values.
  const clientStats = useMemo(() => {
    const now = new Date();
    const startOfDay = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate()
    );
    const startOfWeek = new Date(startOfDay);
    startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());

    let todayCount = 0;
    let weekCount = 0;
    let assignedCount = 0;
    let responseMillisTotal = 0;
    let responseCount = 0;

    for (const l of leads) {
      if (!l.capturedAt) continue;
      const captured = new Date(l.capturedAt);
      if (Number.isNaN(captured.getTime())) continue;
      if (captured >= startOfDay) todayCount += 1;
      if (captured >= startOfWeek) weekCount += 1;
      if (l.assignedBrokerId) assignedCount += 1;
      if (l.firstContactedAt) {
        const contacted = new Date(l.firstContactedAt);
        if (!Number.isNaN(contacted.getTime())) {
          responseMillisTotal += Math.max(0, contacted.getTime() - captured.getTime());
          responseCount += 1;
        }
      }
    }

    const assignmentCoveragePct =
      leads.length > 0 ? Math.round((assignedCount / leads.length) * 100) : 0;
    const avgResponseMinutes =
      responseCount > 0
        ? Math.round(responseMillisTotal / responseCount / 60000)
        : null;

    return {
      todayCount,
      weekCount,
      avgResponseMinutes,
      assignmentCoveragePct,
    };
  }, [leads]);

  // Use server stats when available; fall back to client-derived otherwise.
  const inboxStats = serverStats
    ? {
        todayCount: serverStats.todayCount,
        weekCount: serverStats.weekCount,
        avgResponseMinutes: serverStats.avgResponseMinutes,
        assignmentCoveragePct: serverStats.assignmentCoveragePercent,
      }
    : clientStats;

  const handleRowUpdate = useCallback(
    (args: {
      leadId: string;
      status?: LeadStatus;
      assignedBrokerId?: string | null;
      assignedBrokerName?: string | null;
    }) => updateLead(args),
    [updateLead]
  );

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Inbox}
        title="Leads"
        subtitle="New enquiries from your website forms, ad lead handlers, and manual entries"
        badge="Beta"
        onRefresh={refresh}
        isRefreshing={loading}
        actions={
          canAddLead || canManageWebhook ? (
            <div className="inline-flex items-center gap-2">
              {canAddLead && (
                <button
                  onClick={() => setAddLeadOpen(true)}
                  className="px-3 py-1.5 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm inline-flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add lead
                </button>
              )}
              {canManageWebhook && (
                <button
                  onClick={() => setWebhookOpen(true)}
                  className="px-3 py-1.5 rounded-md bg-gray-700 hover:bg-gray-600 text-white text-sm inline-flex items-center gap-2"
                >
                  <Settings2 className="w-4 h-4" />
                  Webhook URL
                </button>
              )}
            </div>
          ) : null
        }
      />

      {/* Primary stats (mockup §3 shape) — Leads Today / This Week /
          Avg Response Time / Assignment Coverage. Secondary status counts
          remain accessible via the filter tabs below. */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="rounded-lg border border-gray-700 bg-gray-900/40 p-4">
          <div className="text-[11px] uppercase tracking-wider text-gray-500">
            Leads Today
          </div>
          <div className="mt-1 text-3xl font-bold text-white">
            {inboxStats.todayCount}
          </div>
          <div className="mt-1 text-[11px] text-blue-300">Auto-assigned on arrival</div>
        </div>
        <div className="rounded-lg border border-gray-700 bg-gray-900/40 p-4">
          <div className="text-[11px] uppercase tracking-wider text-gray-500">
            Last 7 Days
          </div>
          <div className="mt-1 text-3xl font-bold text-white">
            {inboxStats.weekCount}
          </div>
          {/* The server counts a ROLLING 7 days, not a calendar week, so the
              label said "since Sunday" over a figure that meant something
              else. Label and maths now agree. */}
          <div className="mt-1 text-[11px] text-emerald-300">
            {inboxStats.weekCount === 0
              ? 'No leads captured in the last 7 days'
              : `${inboxStats.weekCount} captured in the last 7 days`}
          </div>
        </div>
        <div className="rounded-lg border border-gray-700 bg-gray-900/40 p-4">
          <div className="text-[11px] uppercase tracking-wider text-gray-500">
            Avg Response Time
          </div>
          <div className="mt-1 text-3xl font-bold text-white">
            {inboxStats.avgResponseMinutes === null
              ? '—'
              : `${inboxStats.avgResponseMinutes} min`}
          </div>
          <div className="mt-1 text-[11px] text-amber-300">
            First touch to contacted
          </div>
        </div>
        <div className="rounded-lg border border-gray-700 bg-gray-900/40 p-4">
          <div className="text-[11px] uppercase tracking-wider text-gray-500">
            Assignment Coverage
          </div>
          <div className="mt-1 text-3xl font-bold text-white">
            {inboxStats.assignmentCoveragePct}%
          </div>
          <div
            className={`mt-1 text-[11px] ${
              inboxStats.assignmentCoveragePct === 100
                ? 'text-emerald-300'
                : 'text-gray-400'
            }`}
          >
            {inboxStats.assignmentCoveragePct === 100
              ? 'All leads auto-assigned'
              : 'Some leads unassigned'}
          </div>
        </div>
      </div>

      {/* Secondary status breakdown tab counts (compact) */}
      <div className="flex flex-wrap gap-2 text-[11px] text-gray-400">
        {(['new', 'contacted', 'qualified', 'converted', 'disqualified'] as LeadStatus[]).map(
          (s) => (
            <span
              key={s}
              className="px-2 py-0.5 rounded-md bg-gray-800/60 border border-gray-700"
            >
              <span className="uppercase tracking-wider">{s}</span>:{' '}
              <span className="text-white font-semibold">{statusCounts[s]}</span>
            </span>
          )
        )}
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex flex-wrap gap-2">
          {STATUS_TABS.map((t) => {
            const active = t.key === statusFilter;
            return (
              <button
                key={t.key}
                onClick={() => setStatusFilter(t.key)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium border transition-colors ${
                  active
                    ? 'bg-blue-600 border-blue-600 text-white'
                    : 'bg-gray-900/40 border-gray-700 text-gray-300 hover:text-white hover:border-gray-500'
                }`}
              >
                {t.label}
              </button>
            );
          })}
        </div>
        {!isBrokerFamily && (
          <label className="inline-flex items-center gap-2 text-xs text-gray-300 cursor-pointer">
            <input
              type="checkbox"
              checked={assignedToMe}
              onChange={(e) => setAssignedToMe(e.target.checked)}
              className="accent-blue-500"
            />
            Mine only
          </label>
        )}
        {tagsOn && (
          <EntityTagFilter
            registry={leadTags.registry}
            registryError={leadTags.registryError}
            onRetry={leadTags.retry}
            value={tagNorm}
            onChange={setTagNorm}
          />
        )}
      </div>

      {tagsOn && (
        <BulkTagBar
          selectedCount={tagSel.selected.size}
          tags={leadTags.registry ?? []}
          busy={tagSel.busy}
          onApply={(add, remove) => void tagSel.apply(add, remove)}
          onClear={tagSel.clear}
        />
      )}
      {tagsOn && tagSel.notice && (
        <div role="status" className="rounded-lg border border-blue-500/30 bg-blue-500/10 p-3 text-sm text-blue-200">
          {tagSel.notice}
        </div>
      )}
      {tagsOn && leadTags.tagsError ? (
        <div role="alert" className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300">
          Tags could not be loaded. {edmErrorMessage(leadTags.tagsError)}{' '}
          <button type="button" className="underline" onClick={leadTags.retry}>
            Retry
          </button>
        </div>
      ) : null}
      {tagsOn && tagSel.error && (
        <div role="alert" className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300">
          {tagSel.error}
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-4 text-red-300 text-sm">
          {error}
        </div>
      )}

      {loading && leads.length === 0 ? (
        <div className="flex items-center justify-center py-16 text-gray-400">
          <RefreshCw className="w-4 h-4 animate-spin mr-2" />
          Loading…
        </div>
      ) : leads.length === 0 ? (
        <div className="rounded-lg border border-gray-700 bg-gray-900/40 p-10 text-center text-gray-400">
          <Phone className="w-8 h-8 text-gray-500 mx-auto mb-2" />
          <p className="text-sm">No leads with this filter yet.</p>
          {canManageWebhook && (
            <p className="text-xs text-gray-500 mt-2">
              Set up the webhook URL on your website form to start capturing.
            </p>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-700">
          <table className="w-full text-sm">
            <thead className="bg-gray-900/60 text-gray-400 text-xs uppercase tracking-wider">
              <tr>
                {tagsOn && (
                  <th className="text-left px-3 py-3">
                    <input
                      type="checkbox"
                      checked={tagSel.allSelected}
                      onChange={tagSel.toggleAll}
                      aria-label="Select all loaded leads"
                      className="accent-blue-500"
                    />
                  </th>
                )}
                <th className="text-left px-3 py-3">Received</th>
                <th className="text-left px-3 py-3">
                  <Users className="inline w-3 h-3 mr-1" />
                  Name
                </th>
                <th className="text-left px-3 py-3">Source</th>
                <th className="text-left px-3 py-3">Campaign</th>
                <th className="text-left px-3 py-3">Assigned To</th>
                <th className="text-left px-3 py-3">Status</th>
                {tagsOn && <th className="text-left px-3 py-3">Tags</th>}
                <th className="text-right px-3 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {leads.map((lead) => (
                <LeadRow
                  key={lead.leadId}
                  lead={lead}
                  companyId={companyId || undefined}
                  currentUid={userData?.uid}
                  currentName={userData?.displayName}
                  currentRole={role || undefined}
                  teamMembers={teamMemberList}
                  scopeOfficeIds={scopeOfficeIds}
                  onUpdate={handleRowUpdate}
                  onDecline={declineLead}
                  onLogContact={setLogContactLead}
                  onOpenDetail={(l) => setSelectedLeadId(l.leadId)}
                  tagging={
                    tagsOn
                      ? {
                          selected: tagSel.selected.has(lead.leadId),
                          onToggle: () => tagSel.toggle(lead.leadId),
                          tags: leadTags.tagsById[lead.leadId],
                        }
                      : undefined
                  }
                />
              ))}
            </tbody>
          </table>
          {hasMore && (
            <div className="flex justify-center border-t border-gray-800 p-3">
              <button
                type="button"
                onClick={loadMore}
                disabled={loadingMore}
                className="px-3 py-1.5 rounded-md border border-gray-600 text-sm text-gray-300 hover:text-white hover:border-gray-400 disabled:opacity-50"
              >
                {loadingMore ? 'Loading…' : 'Load more'}
              </button>
            </div>
          )}
        </div>
      )}

      <WebhookModal
        open={webhookOpen}
        companyId={companyId}
        onClose={() => setWebhookOpen(false)}
        onGetConfig={getWebhookTokenConfig}
        onGenerate={generateWebhookToken}
        onRotate={rotateWebhookToken}
        onEnableMonitoring={enableWebhookMonitoring}
        onSetDisabled={setWebhookDisabled}
      />

      <AddLeadModal
        open={addLeadOpen}
        companyId={companyId}
        onClose={() => setAddLeadOpen(false)}
        onCreate={createManualLead}
      />

      <LogActivityModal
        open={logContactLead !== null}
        onClose={() => setLogContactLead(null)}
        linkedLeadId={logContactLead?.leadId}
        leadStatus={logContactLead?.status}
        prefill={
          logContactLead
            ? {
                clientName:
                  [logContactLead.firstName, logContactLead.lastName]
                    .filter(Boolean)
                    .join(' ') || undefined,
                clientPhone: logContactLead.phone,
                clientEmail: logContactLead.email,
              }
            : undefined
        }
        onSubmit={async ({ markContacted, ...args }) => {
          const lead = logContactLead;
          if (!lead) return;
          await logActivity({ ...args, linkedLeadId: lead.leadId });
          // Auto-advance a NEW lead to 'contacted' when the operator opted in.
          // updateLead idempotently stamps firstContactedAt.
          if (markContacted && lead.status === 'new') {
            await updateLead({ leadId: lead.leadId, status: 'contacted' });
          }
          refresh();
          // Re-fetch the drawer timeline if it's open on this lead.
          setTimelineRefreshSignal((s) => s + 1);
        }}
      />

      <LeadDetailDrawer
        open={selectedLead !== null}
        lead={selectedLead}
        companyId={companyId || undefined}
        canAct={canActOnLead(selectedLead)}
        refreshSignal={timelineRefreshSignal}
        loanLinker={
          // The drawer's one content slot carries the classification panel
          // and, where permitted, the loan linker.
          selectedLead ? (
            <div className="space-y-3">
              <LeadClassificationPanel
                lead={selectedLead}
                companyId={companyId}
                canEdit={canActOnLead(selectedLead)}
                onSave={(classification) => updateLead({ leadId: selectedLead.leadId, classification })}
              />
              {/* Link-to-Loan drives commission attribution → `linkLeadToLoan` is
                  denied for marketing. Hide the control so marketing never sees a
                  button that would only permission-deny. */}
              {canActOnLead(selectedLead) && role !== 'marketing' && role !== 'service_staff' && (
                <LinkLeadToLoanControl
                  linkedLoanId={selectedLead.linkedLoanId}
                  onLink={(loanId) =>
                    linkLeadToLoan({ leadId: selectedLead.leadId, loanId })
                  }
                />
              )}
            </div>
          ) : undefined
        }
        onClose={() => setSelectedLeadId(null)}
        onLogContact={(lead) => setLogContactLead(lead)}
        onAddNote={async (lead, body) => {
          // Notes reuse activityType:'note' + linkedLeadId — they land in the
          // same client_activities stream as calls/meetings (one timeline).
          await logActivity({
            activityType: 'note',
            linkedLeadId: lead.leadId,
            notes: body,
          });
          // Keep the inbox list fresh too (no status change, but cheap).
          refresh();
        }}
      />
    </div>
  );
};

export default LeadsPage;
